import { supabase } from '@/integrations/supabase/client';
import type { Database } from '@/integrations/supabase/types';

export type Mensagem = Database['public']['Tables']['mensagens']['Row'];
export type MensagemInsert = Database['public']['Tables']['mensagens']['Insert'];

export async function listMensagens(clienteId: string): Promise<Mensagem[]> {
  const { data, error } = await supabase
    .from('mensagens')
    .select('*')
    .eq('cliente_potencial_id', clienteId)
    .order('criado_em', { ascending: true });

  if (error) throw error;
  return data ?? [];
}

export interface EnviarMensagemInput {
  dados: MensagemInsert;
  orgId: string;
}

export async function enviarMensagem(input: EnviarMensagemInput): Promise<Mensagem> {
  const { data, error } = await supabase
    .from('mensagens')
    .insert({ ...input.dados, organization_id: input.orgId })
    .select()
    .single();

  if (error) throw error;
  return data;
}

/**
 * Lista as mensagens de media (prontas) de um cliente, mais recentes
 * primeiro. Alimenta a aba "Mídia" da galeria do perfil.
 */
export async function listClienteMedia(clienteId: string): Promise<Mensagem[]> {
  const { data, error } = await supabase
    .from('mensagens')
    .select('*')
    .eq('cliente_potencial_id', clienteId)
    .not('tipo_media', 'is', null)
    .eq('media_status', 'ready')
    .order('criado_em', { ascending: false });

  if (error) throw error;
  return data ?? [];
}

/**
 * Lista as mensagens que contêm um link de um cliente (ambas as direções),
 * mais recentes primeiro. Alimenta a aba "Links" da galeria do perfil.
 */
export async function listClienteLinks(clienteId: string): Promise<Mensagem[]> {
  const { data, error } = await supabase
    .from('mensagens')
    .select('*')
    .eq('cliente_potencial_id', clienteId)
    .eq('tem_link', true)
    .order('criado_em', { ascending: false });

  if (error) throw error;
  return data ?? [];
}

export interface InboxPreview {
  cliente_potencial_id: string;
  conteudo: string;
  criado_em: string;
  tipo_media: string | null;
  unread_count: number;
}

/**
 * Retorna a última mensagem por cliente para uma lista de clienteIds, mais a
 * contagem de mensagens não lidas vindas do cliente. Faz UMA query que pesca
 * todas as mensagens recentes desses clientes; depois agrupa client-side.
 *
 * Para inboxes muito grandes o ideal é uma view com window function — para
 * agora isto é simples e RLS-safe.
 */
export async function listInboxPreviews(
  clienteIds: string[],
): Promise<Record<string, InboxPreview>> {
  if (clienteIds.length === 0) return {};

  const { data, error } = await supabase
    .from('mensagens')
    .select('cliente_potencial_id, conteudo, criado_em, tipo_media, remetente, lida')
    .in('cliente_potencial_id', clienteIds)
    .order('criado_em', { ascending: false });

  if (error) throw error;

  const map: Record<string, InboxPreview> = {};
  for (const row of data ?? []) {
    const id = row.cliente_potencial_id;
    if (!map[id]) {
      map[id] = {
        cliente_potencial_id: id,
        conteudo: row.conteudo ?? '',
        criado_em: row.criado_em ?? '',
        tipo_media: row.tipo_media,
        unread_count: 0,
      };
    }
    if (row.remetente === 'cliente' && !row.lida) {
      map[id].unread_count += 1;
    }
  }
  return map;
}

/** Conta as conversas da org com mensagens do cliente por ler (via RPC). */
export async function contarConversasNaoLidas(): Promise<number> {
  const { data, error } = await supabase.rpc('contar_conversas_nao_lidas');
  if (error) throw error;
  return (data as number | null) ?? 0;
}
